import React, { useCallback, useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import moment from "moment";
import { UserService } from "../../../services/UserService";
import { apiMessage } from "../../../api/apiMessage";
import useAlive from "../../../hooks/useAlive";

const DAYS = 30;
const TABLE_ROWS = 25;

// The old routes, each always in the same colour (app-admin.css).
const ROUTES = [
  { key: "routine", label: "Routines", color: "--hx-adm-series-1" },
  { key: "scene", label: "Scenes", color: "--hx-adm-series-2" },
  { key: "rule", label: "Rules", color: "--hx-adm-series-3" },
];

const css = (name, fallback) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;

// The last DAYS days as the API writes them (UTC dates), oldest first.
const lastDays = () => {
  const now = new Date();
  return Array.from({ length: DAYS }, (_, index) =>
    new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - (DAYS - 1 - index)))
      .toISOString()
      .slice(0, 10)
  );
};

const dayLabel = (day) => moment.utc(day).format("D MMM");

// Calls old apps still make to the old routine, scene and rule routes, to see
// when they're gone: totals, a column per day split by route, and the rows.
function LegacyUsage() {
  const alive = useAlive();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await UserService.getLegacyRouteUsage(DAYS);
      if (alive.current) setRows(Array.isArray(result) ? result : []);
    } catch (err) {
      if (alive.current) setError(apiMessage(err, "Couldn't load the old routes' usage."));
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [alive]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = useMemo(() => {
    if (!rows) return null;
    const days = lastDays();
    const weekStart = days[days.length - 7];
    const byRoute = Object.fromEntries(ROUTES.map(({ key }) => [key, Object.fromEntries(days.map((day) => [day, 0]))]));
    let total = 0;
    let week = 0;
    let lastDay = null;
    rows.forEach((row) => {
      const count = Number(row.count) || 0;
      total += count;
      if (row.day >= weekStart) week += count;
      if (count && (!lastDay || row.day > lastDay)) lastDay = row.day;
      if (byRoute[row.route] && row.day in byRoute[row.route]) byRoute[row.route][row.day] += count;
    });
    return { days, byRoute, total, week, lastDay };
  }, [rows]);

  const chart = useMemo(() => {
    if (!summary || !summary.total) return null;
    const dark = document.documentElement.dataset.theme === "dark";
    const muted = css("--hx-muted", "#8a8a8a");
    return {
      series: ROUTES.map(({ key, label }) => ({ name: label, data: summary.days.map((day) => summary.byRoute[key][day]) })),
      options: {
        chart: { type: "bar", stacked: true, toolbar: { show: false }, animations: { enabled: false }, fontFamily: "Poppins, sans-serif", foreColor: muted },
        colors: ROUTES.map(({ color }, index) => css(color, ["#2a78d6", "#eb6834", "#1baf7a"][index])),
        plotOptions: { bar: { columnWidth: "60%" } },
        // A 2px gap in the card's colour between stacked segments and columns.
        stroke: { show: true, width: 2, colors: [css("--hx-surface", "#ffffff")] },
        dataLabels: { enabled: false },
        legend: { position: "top", horizontalAlign: "left", labels: { colors: css("--hx-text", "#000") }, markers: { radius: 2 } },
        xaxis: {
          categories: summary.days.map(dayLabel),
          labels: { rotate: -45, hideOverlappingLabels: true, style: { fontSize: "11px" } },
          axisBorder: { color: dark ? "#1d3445" : "#e8edf3" },
          axisTicks: { show: false },
        },
        yaxis: { min: 0, forceNiceScale: true, labels: { formatter: (value) => Math.round(value).toLocaleString() } },
        grid: { borderColor: dark ? "#1d3445" : "#e8edf3", strokeDashArray: 0 },
        tooltip: {
          shared: true,
          intersect: false,
          theme: dark ? "dark" : "light",
          y: { formatter: (value) => `${Number(value).toLocaleString()} call${Number(value) === 1 ? "" : "s"}` },
        },
        states: { hover: { filter: { type: "lighten", value: 0.08 } } },
      },
    };
  }, [summary]);

  if (error && !rows) {
    return (
      <div className="hx-adm-empty" role="alert">
        {error}
        <button type="button" className="hx-btn" onClick={load} disabled={loading}>
          Try again
        </button>
      </div>
    );
  }
  if (!summary) return <div className="hx-skel" style={{ height: 220 }} aria-label="Loading" />;

  const shown = showAll ? rows : rows.slice(0, TABLE_ROWS);

  return (
    <div className={loading ? "hx-adm-refreshing" : ""}>
      <div className="hx-adm-tiles">
        <div className="hx-adm-tile">
          <span className="hx-adm-tile-value">{summary.total.toLocaleString()}</span>
          <span className="hx-adm-tile-label">Calls in {DAYS} days</span>
        </div>
        <div className="hx-adm-tile">
          <span className="hx-adm-tile-value">{summary.week.toLocaleString()}</span>
          <span className="hx-adm-tile-label">In the last 7 days</span>
        </div>
        <div className="hx-adm-tile">
          <span className="hx-adm-tile-value">{summary.lastDay ? dayLabel(summary.lastDay) : "—"}</span>
          <span className="hx-adm-tile-label">Last call</span>
        </div>
      </div>

      {chart ? (
        <div className="hx-adm-chart">
          <Chart type="bar" height={260} series={chart.series} options={chart.options} />
        </div>
      ) : (
        <p className="hx-adm-empty">No old app called the old routes in the last {DAYS} days.</p>
      )}

      {rows.length ? (
        <>
          <div className="hx-adm-table-wrap">
            <table className="hx-adm-table">
              <caption className="hx-adm-sr">Calls to the old routes per day, route, method and app</caption>
              <thead>
                <tr>
                  <th scope="col">Day</th>
                  <th scope="col">Route</th>
                  <th scope="col">Method</th>
                  <th scope="col">App</th>
                  <th scope="col" className="is-number">
                    Calls
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.map((row, index) => (
                  <tr key={`${row.day}-${row.route}-${row.method}-${row.agent}-${index}`}>
                    <td>{dayLabel(row.day)}</td>
                    <td>{ROUTES.find((route) => route.key === row.route)?.label ?? row.route}</td>
                    <td>{row.method}</td>
                    <td className="hx-adm-agent">{row.agent || "—"}</td>
                    <td className="is-number">{Number(row.count).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rows.length > TABLE_ROWS ? (
            <button type="button" className="hx-adm-link" onClick={() => setShowAll(!showAll)}>
              {showAll ? "Show fewer" : `Show all ${rows.length} rows`}
            </button>
          ) : null}
        </>
      ) : null}

      <div className="hx-adm-actions">
        <button type="button" className="hx-btn is-ghost" onClick={load} disabled={loading}>
          {loading ? "Loading…" : "Reload"}
        </button>
      </div>
      {error ? (
        <p className="hx-adm-note is-error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export default LegacyUsage;
